import { useQuery, useLazyQuery, useMutation } from "@apollo/client"
import cn from "classnames"
import React, { useRef, useMemo, useEffect, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import BasketModal, { basketStateDoc, setBasketStateDoc, BasketFragmentDoc, basketStatusDoc } from "./basket_modal"
import { graphql, getFragment } from "@/api"
import { type AssortmentCategoriesQuery, ClientRole, NavbarLinksTarget } from "@/api/graphql"
import ClientData from "@/common/client_data"
import DotSpinner from "@/common/components/dot_spinner"
import env from "@/common/env"
import useMedia from "@/common/hooks/use_media"
import storageLocal from "@/common/storage"
import tailwindTheme from "@/common/styles/theme.module.css"
import { toggleScroll } from "@/common/util"
import {
  searchfieldTemplate,
  searchresultsTemplate,
  noResultsTemplate,
  infiniteScrollTemplate,
} from "@/menu/components/search_templates"
import {
  ChevronDown,
  Menu,
  Search,
  ProfileOutline,
  HeadphonesOutline,
  CloseLarge,
  DocumentTextOutline,
  ChevronLeftLarge,
  ChevronRightLarge,
  ArrowRight,
  Reset,
  ArrowDown,
  ArrowUp,
  ChevronUpLarge,
  ChevronDownLarge,
  CloseOutline,
  CartFill,
  CartOutline,
  ProfileFill,
} from "@/skylticons"
import "@/menu/styles/search.css"

const assortmentCategoriesDoc = graphql(`
  query AssortmentCategories($locale: String!, $business: Boolean!) {
    assortmentCategories(locale: $locale, business: $business) {
      id
      signCategoryId
      name
      path
      locale
      order
      business

      tags {
        id
        assortmentCategoryId
        signTagId
        pagename
        path
        locale
        order
        targetCategory
        business
      }
    }
  }
`)

const navbarsDoc = graphql(`
  query NavbarLinks($locale: String, $role: ClientRole) {
    navbarLinks(locale: $locale, role: $role) {
      id
      path
      title
      business
      locale
      kind
      role

      page {
        id
        name
        path
      }

      signTag {
        id
        pagename
        url
      }

      signCategory {
        id
        name
        path
      }
    }
  }
`)

type Categories = AssortmentCategoriesQuery["assortmentCategories"]

interface RoleSwitchProps {
  businessRole: boolean
  mobile?: boolean
}

const RoleSwitch: React.FC<RoleSwitchProps> = ({ businessRole, mobile }) => {
  const { t } = useTranslation()
  const desktop = !mobile

  const roleButtons = () => {
    return (
      <>
        <a
          href="/set_client_role?role=consumer"
          data-gtm="role-switch-consumer"
          className={cn({
            "mb-0 text-sm font-semibold text-white opacity-50 hover:underline": desktop,
            "paragraph-sm mb-0 font-medium opacity-50": mobile,
            active: !businessRole,
          })}
        >
          {t("customer.consumer")}
        </a>
        <span
          className={cn({
            "mx-1 mb-0 text-sm font-semibold text-white opacity-100": desktop,
            "paragraph-sm mx-2 mb-0": mobile,
          })}
        >
          /
        </span>
        <a
          href="/set_client_role?role=business"
          data-gtm="role-switch-business"
          className={cn({
            "mb-0 text-sm font-semibold text-white opacity-50 hover:underline": desktop,
            "paragraph-sm mb-0 font-medium opacity-50": mobile,
            active: businessRole,
          })}
        >
          {t("customer.business")}
        </a>
      </>
    )
  }

  return (
    <div className={cn({ "role-switch": desktop, "role-switch-mobile mb-4 flex h-12 items-center pl-4": mobile })}>
      {mobile ? <span>{roleButtons()}</span> : roleButtons()}
    </div>
  )
}

interface LanguageDropdownProps {
  mobile?: boolean
  langNl: string
  langFr: string
  langDe: string
  langIt: string
}

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({ mobile, langNl, langFr, langDe, langIt }) => {
  const { t, i18n } = useTranslation()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const desktop = !mobile

  return (
    <div className={cn({ "langrow text-center": desktop, "hidden-lg px-6 pb-8": mobile })}>
      <ul
        className={cn("dropdown lang-dropdown", { "m-0 flex items-center justify-center p-0 text-sm": desktop })}
        data-gtm="language-dropdown"
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        <li
          className={cn("dropdown-selected", {
            "flex items-center justify-center p-0 font-semibold": desktop,
          })}
          aria-label={t("menu.language")}
          role="button"
          tabIndex={0}
          aria-haspopup="true"
        >
          <span className={cn({ "paragraph-xs mb-0 font-semibold": desktop, "paragraph-sm": mobile })}>
            {t("site.language.name")}
          </span>
          <span className={cn("dropdown-arrow", { expanded: dropdownOpen })}>
            <ChevronDown className={cn({ "!relative !right-0 !m-0": desktop })} />
          </span>
        </li>
        <li
          className={cn("dropdown-list-container z-40", { "top-6": desktop })}
          aria-expanded={dropdownOpen}
          role="list"
        >
          <ul className={cn("dropdown-list", { open: dropdownOpen })}>
            {i18n.language.includes("BE") && (
              <>
                <a href="/fr" className="dropdown-link">
                  <li
                    className={cn("dropdown-list-item", {
                      "active-item": i18n.language === "fr-BE",
                      "text-left": desktop,
                    })}
                    tabIndex={0}
                  >
                    <span className={cn("paragraph-sm", { "font-normal": desktop })}>{langFr}</span>
                  </li>
                </a>
                <a href="/nl" className="dropdown-link">
                  <li
                    className={cn("dropdown-list-item", {
                      "active-item": i18n.language === "nl-BE",
                      "text-left": desktop,
                    })}
                    tabIndex={0}
                  >
                    <span className={cn("paragraph-sm", { "font-normal": desktop })}>{langNl}</span>
                  </li>
                </a>
              </>
            )}
            {i18n.language.includes("CH") && (
              <>
                <a href="/de" className="dropdown-link">
                  <li
                    className={cn("dropdown-list-item", {
                      "active-item": i18n.language === "de-CH",
                      "text-left": desktop,
                    })}
                    tabIndex={0}
                  >
                    <span className={cn("paragraph-sm", { "font-normal": desktop })}>{langDe}</span>
                  </li>
                </a>
                <a href="/fr" className="dropdown-link">
                  <li
                    className={cn("dropdown-list-item", {
                      "active-item": i18n.language === "fr-CH",
                      "text-left": desktop,
                    })}
                    tabIndex={0}
                  >
                    <span className={cn("paragraph-sm", { "font-normal": desktop })}>{langFr}</span>
                  </li>
                </a>
                <a href="/it" className="dropdown-link">
                  <li
                    className={cn("dropdown-list-item", {
                      "active-item": i18n.language === "it-CH",
                      "text-left": desktop,
                    })}
                    tabIndex={0}
                  >
                    <span className={cn("paragraph-sm", { "font-normal": desktop })}>{langIt}</span>
                  </li>
                </a>
              </>
            )}
          </ul>
        </li>
      </ul>
    </div>
  )
}

interface TrustpilotWidgetProps {
  trustpilotBizId: string
  classes?: string
  height: string
  width: string
}

const TrustpilotWidget: React.FC<TrustpilotWidgetProps> = ({ trustpilotBizId, classes, height, width }) => {
  const { i18n } = useTranslation()
  const trustpilotRef = useRef(null)

  useEffect(() => {
    if (window.Trustpilot) {
      window.Trustpilot.loadFromElement(trustpilotRef.current, true)
    }
  }, [])

  return (
    <div
      ref={trustpilotRef}
      className={cn("truspilot-widget", classes)}
      data-businessunit-id={trustpilotBizId}
      data-locale={i18n.language}
      data-style-height={height}
      data-style-width={width}
      data-template-id="5406e65db0d04a09e042d5fc"
      data-theme="dark"
    >
      <a href="https://se.trustpilot.com/review/skyltmax.se" target="_blank" rel="noreferrer"></a>
    </div>
  )
}

interface PreMenurowProps {
  trustpilotEnabled: boolean
  trustpilotBizId: string
  businessRole: boolean
  langNl: string
  langFr: string
  langDe: string
  langIt: string
}

const PreMenurow: React.FC<PreMenurowProps> = ({
  trustpilotEnabled,
  trustpilotBizId,
  businessRole,
  langNl,
  langFr,
  langDe,
  langIt,
}) => {
  const { t, i18n } = useTranslation()

  return (
    <div
      className={cn("pre-menurow relative hidden h-10 items-center justify-end px-6 xl:flex", {
        production: env.PRODUCTION && ClientData.developer,
      })}
    >
      <div className="trustpilot flex" aria-hidden="true">
        {trustpilotEnabled && (
          <>
            <TrustpilotWidget trustpilotBizId={trustpilotBizId} height="25px" width="250px" />
            <div className="my-0 ml-1 mr-4 h-6 w-px bg-white p-0 opacity-30"></div>
          </>
        )}
      </div>
      <a href={t("route.contact")} className="contact subtle flex items-center" data-gtm="contact-link">
        <span className="mb-0 text-sm font-semibold text-white">{t("menu.contact")}</span>
      </a>
      {(i18n.language.includes("BE") || i18n.language.includes("CH")) && (
        <>
          <div className="mx-4 my-0 h-6 w-px bg-white p-0 opacity-30"></div>
          <LanguageDropdown langNl={langNl} langFr={langFr} langDe={langDe} langIt={langIt} />
        </>
      )}
      <div className="mx-4 my-0 h-6 w-px bg-white p-0 opacity-30"></div>
      <RoleSwitch businessRole={businessRole} />
    </div>
  )
}

interface SearchFieldProps {
  ui: any
  searchFieldRef: React.RefObject<HTMLDivElement>
  addsearchCategories: string
}

const SearchField: React.FC<SearchFieldProps> = ({ ui, searchFieldRef, addsearchCategories }) => {
  const { t } = useTranslation()
  const id = "addsearch-searchfield"

  useEffect(() => {
    if (!ui) {
      return
    }

    ui.searchField({
      containerId: id,
      template: searchfieldTemplate(t, addsearchCategories),
      searchAsYouType: true,
      autofocus: false,
    })
  }, [ui, id, t, addsearchCategories])

  return <div id={id} ref={searchFieldRef}></div>
}

interface SearchResultsProps {
  ui: any
  resultsRef: React.RefObject<HTMLDivElement>
}

const SearchResults: React.FC<SearchResultsProps> = ({ ui, resultsRef }) => {
  const { t } = useTranslation()
  const id = "addsearch-results"

  useEffect(() => {
    if (!ui) {
      return
    }

    ui.searchResults({
      containerId: id,
      template: searchresultsTemplate(),
      template_noresults: noResultsTemplate(t),
    })
  }, [ui, id, t])

  return (
    <div
      id={id}
      ref={resultsRef}
      className="z-40 -ml-4 mt-1 hidden max-h-[62vh] w-[calc(100%_+_32px)] overflow-y-auto rounded-t-lg border border-t-1 border-white-10 shadow-lg sm:ml-0 sm:max-h-[70vh] sm:w-auto"
    ></div>
  )
}

interface SearchInfiniteScrollProps {
  ui: any
  infiniteScrollRef: React.RefObject<HTMLDivElement>
  addsearchImport: any
}

const SearchInfiniteScroll: React.FC<SearchInfiniteScrollProps> = ({ ui, infiniteScrollRef, addsearchImport }) => {
  const { t } = useTranslation()
  const id = "loadmore"

  useEffect(() => {
    if (!ui || !addsearchImport) {
      return
    }

    ui.loadMore({
      containerId: id,
      type: addsearchImport.LOAD_MORE_TYPE.INFINITE_SCROLL,
      infiniteScrollElement: document.querySelector("#addsearch-results"),
      template: infiniteScrollTemplate(t),
    })
  }, [ui, id, t, addsearchImport])

  return <div id={id} ref={infiniteScrollRef}></div>
}

interface MenuSearchProps {
  addsearchCategories: string
  classes?: string
  setSearchActive?: (active: boolean) => void
  closeAssortment?: () => void
}

const MenuSearch: React.FC<MenuSearchProps> = ({ addsearchCategories, classes, setSearchActive, closeAssortment }) => {
  const { t, i18n } = useTranslation()
  const searchFieldRef = useRef<HTMLDivElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)
  const infiniteScrollRef = useRef<HTMLDivElement>(null)
  const [addsearch, setAddsearch] = useState<any>()
  const [addsearchImport, setAddsearchImport] = useState<any>()
  const [searchLoaded, setSearchLoaded] = useState(false)

  useEffect(() => {
    async function getAddsearchUI() {
      try {
        const addsearchClientImported = (await import("addsearch-js-client")).default
        const addSearchClient = new addsearchClientImported("7025a3437545e541de2ba6a48d6bb0ee")
        addSearchClient.setThrottleTime(300)
        addSearchClient.setCategoryFilters(addsearchCategories)
        addSearchClient.setLanguage(i18n.language)

        const addsearchImported = await import("addsearch-search-ui")
        const AddSearchUI = addsearchImported.default
        setAddsearchImport(addsearchImported)

        const addsearchNew = new AddSearchUI(addSearchClient)
        setAddsearch(addsearchNew)

        addsearchNew.start()
        setSearchLoaded(true)
      } catch (error) {
        console.error(error)
      }
    }

    getAddsearchUI()
  }, [addsearchCategories, i18n.language])

  const addSearchUI = useMemo(() => {
    if (!addsearch) {
      return
    }
    addsearch.start()

    return addsearch
  }, [addsearch])

  useEffect(() => {
    if (!addsearch) {
      return
    }

    const searchInput = document.querySelector<HTMLInputElement>(`#addsearch-searchfield .search-field-wrapper input`)
    const closeBtn = document.querySelector<HTMLSpanElement>(`#addsearch-searchfield .close-icon span`)
    const searchResults = document.querySelector<HTMLDivElement>("#addsearch-results")
    const loadmore = document.querySelector<HTMLDivElement>("#loadmore")

    if (searchInput) {
      searchInput.onfocus = () => {
        if (closeAssortment) {
          closeAssortment()
        }

        if (searchInput.value.length > 0) {
          searchResults?.classList.remove("hidden")
          closeBtn?.classList.remove("hidden")
          loadmore?.classList.remove("hidden")
        }
      }
    }

    const handleKeyUp = (e: MouseEvent) => {
      if (searchFieldRef.current?.contains(e.target as HTMLElement)) {
        const input = e.target as HTMLInputElement

        if (input) {
          if (input.value.length == 0) {
            closeBtn?.classList.add("hidden")
            resultsRef.current?.classList.add("hidden")
          } else {
            if (closeAssortment) {
              closeAssortment()
            }
            if (setSearchActive) {
              setSearchActive(true)
            }

            closeBtn?.classList.remove("hidden")
            resultsRef.current?.classList.remove("hidden")
          }
        }
      }
    }

    document.addEventListener("keyup", handleKeyUp)

    return () => {
      document.removeEventListener("keyup", handleKeyUp)
    }
  }, [addsearch, setSearchActive, closeAssortment])

  useEffect(() => {
    if (!addsearch) {
      return
    }

    const closeBtn = document.querySelector<HTMLSpanElement>(`#addsearch-searchfield .close-icon span`)
    const searchInput = document.querySelector<HTMLInputElement>("#search-input")

    const handleSearchClose = (e: MouseEvent) => {
      if (closeBtn?.contains(e.target as HTMLElement)) {
        if (e.isTrusted) {
          addsearch.search("")
        }

        closeBtn?.classList.add("hidden")
        resultsRef.current?.classList.add("hidden")

        if (!e.isTrusted) {
          searchInput?.blur()
        }
      }

      if (setSearchActive) {
        setSearchActive(false)
      }
    }

    closeBtn?.addEventListener("click", handleSearchClose)

    return () => {
      closeBtn?.removeEventListener("click", handleSearchClose)
    }
  })

  useEffect(() => {
    const searchResults = document.querySelector<HTMLDivElement>("#addsearch-results")
    const closeBtn = document.querySelector<HTMLSpanElement>(`#addsearch-searchfield .close-icon span`)
    const loadmore = document.querySelector<HTMLDivElement>("#loadmore")

    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchFieldRef.current &&
        !searchFieldRef.current.contains(event.target as Node) &&
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node) &&
        loadmore &&
        !loadmore.contains(event.target as Node)
      ) {
        searchResults?.classList.add("hidden")
        closeBtn?.classList.add("hidden")
        loadmore.classList.add("hidden")

        if (setSearchActive) {
          setSearchActive(false)
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  })

  return (
    <div id="main_search" className={cn("ml-0 mr-0 h-12 w-full max-w-[640px] px-4 md:px-0 xl:ml-12 xl:mr-1", classes)}>
      {searchLoaded ? (
        <SearchField ui={addSearchUI} searchFieldRef={searchFieldRef} addsearchCategories={addsearchCategories} />
      ) : (
        <div className="addsearch-searchfield-container">
          <form className="addsearch-searchfield" autoComplete="off" action="?" role="search">
            <div className="search-field-wrapper relative flex h-12 w-full justify-between rounded border-1 bg-white-5 focus-within:border-primary focus-within:outline focus-within:outline-2 focus-within:outline-aqua-200">
              <span className="left-icon m-auto flex h-full min-w-[40px] items-center justify-center border-r xl:hidden">
                <Search className="h-6 w-6" />
              </span>
              <input
                type="text"
                className="h-full w-full border-0 bg-white-5 px-2 py-3 hover:!border-0 focus:!border-0 focus:!shadow-none hover:focus:border-0 xl:px-4"
                placeholder={t("menu.search_placeholder")}
                aria-label="Search field"
                data-categories="${categories}"
              />
              <span className="flex items-center">
                <span className="close-icon m-auto mx-3 cursor-pointer">
                  <span className="hidden">
                    <CloseLarge />
                    <CloseOutline className="xl:hidden" />
                  </span>
                </span>
                <span className="m-auto hidden h-6 border-l-1 border-white-20 xl:block"></span>
                <span className="left-icon m-auto hidden cursor-pointer items-center px-3 xl:flex">
                  <Search className="h-6 w-6" />
                </span>
              </span>
            </div>
          </form>
        </div>
      )}

      <SearchResults ui={addSearchUI} resultsRef={resultsRef} />
      <SearchInfiniteScroll ui={addSearchUI} infiniteScrollRef={infiniteScrollRef} addsearchImport={addsearchImport} />
    </div>
  )
}

interface MenurowProps {
  siteUrl: string
  currentUser: boolean
  loadBasket: boolean
  showEmpty: boolean
  cartCount: number
  addsearchCategories: string
  logoPath: string
  setMobileSearchOpen: () => void
  mobileSearchOpen: boolean
  setMobileMenuOpen: () => void
  toggleAssortment: () => void
  assortmentOpen: boolean
  categories: Categories
  closeAssortment: () => void
  defaultCategoryId: string
  classes: string
  menurowRef: React.Ref<HTMLDivElement>
  businessRole: boolean
  activePage: string
  cartUrl: string
  menuRowBottom: number
  orderId?: string
}

const Menurow: React.FC<MenurowProps> = ({
  siteUrl,
  currentUser,
  loadBasket,
  showEmpty,
  cartCount,
  addsearchCategories,
  logoPath,
  setMobileSearchOpen,
  mobileSearchOpen,
  setMobileMenuOpen,
  toggleAssortment,
  assortmentOpen,
  categories,
  closeAssortment,
  defaultCategoryId,
  classes,
  menurowRef,
  businessRole,
  activePage,
  cartUrl,
  menuRowBottom,
  orderId,
}) => {
  const { t, i18n } = useTranslation()
  const [loaded, setLoaded] = useState(false)
  const [searchActive, setSearchActive] = useState(false)

  const results = useQuery(navbarsDoc, {
    variables: { locale: i18n.language, role: businessRole ? ClientRole.business : ClientRole.consumer },
    context: { retry: true },
  })

  const navbarLinks = results.data?.navbarLinks

  useEffect(() => {
    setLoaded(true)
  }, [])

  const [queryBasket, { error, loading, data, called, refetch }] = useLazyQuery(basketStatusDoc)
  const basketState = useQuery(basketStateDoc)
  const [setBasketState] = useMutation(setBasketStateDoc)
  const [refetchLoading, setRefetchLoading] = useState(false)
  const desktop = useMedia(`(min-width: ${tailwindTheme.screensXl})`, false)

  const open = basketState.data?.basketState?.open
  const showAddedNotice = basketState.data?.basketState?.addedNotice || false
  const excelImportNotice = basketState.data?.basketState?.excelImportNotice || false
  const reload = basketState.data?.basketState?.reload || false

  const modalOpen = open || assortmentOpen
  const customerZoneActive = activePage === "customer" && !modalOpen

  // load basket contents either immediately or only on first open
  useEffect(() => {
    if (called) {
      return
    }

    if (loadBasket || open) {
      queryBasket({
        variables: {
          id: orderId,
        },
      })
    }
  }, [called, loadBasket, open, orderId, queryBasket])

  useEffect(() => {
    if (reload) {
      setBasketState({ variables: { open: true, reload: false, excelImportNotice: excelImportNotice } })

      if (called) {
        setRefetchLoading(true)
        refetch({ id: orderId }).finally(() => setRefetchLoading(false))
      } else {
        queryBasket({
          variables: {
            id: orderId,
          },
        })
      }
    }
  }, [reload, called, queryBasket, refetch, setBasketState, orderId, excelImportNotice])

  const toggleBasket = useCallback(() => {
    setBasketState({ variables: { open: !open, excelImportNotice: excelImportNotice } })
  }, [open, setBasketState, excelImportNotice])

  const closeExcelImportNotice = useCallback(() => {
    setBasketState({ variables: { open: true, excelImportNotice: false } })
  }, [setBasketState])

  if (error) {
    return <>Oops...</>
  }

  const basketStatus = getFragment(BasketFragmentDoc, data?.basketStatus)
  const numberOfProducts = basketStatus?.basketItems
    .map(item => item.totalCount)
    .reduce((accumulator, currentValue) => accumulator + currentValue, 0)

  const count = numberOfProducts === 0 || numberOfProducts ? numberOfProducts : cartCount

  return (
    <>
      <div className={cn("menurow", classes)} ref={menurowRef}>
        <div className="flex h-12 w-full justify-between xl:h-16">
          <div className="mobile-navbar">
            <div
              className="navbar-toggle"
              data-gtm="mobile-open-menu"
              onClick={() => {
                setMobileMenuOpen()
                toggleScroll(true)
              }}
            >
              <Menu />
            </div>
            <div className="search-toggle" data-gtm="open-search" onClick={() => setMobileSearchOpen()}>
              <Search />
            </div>
          </div>
          <nav className="header">
            <a href={siteUrl} className="logo" data-gtm="logo-link">
              <img src={logoPath} alt="logo" className="sm:min-w-[160px]" />
            </a>
            <>
              {navbarLinks?.map(item => {
                if (item.kind == NavbarLinksTarget.assortment) {
                  return (
                    <span
                      id="assortment"
                      key={item.id}
                      className={cn("link-button headerlink examples ml-2 mr-5 whitespace-nowrap", {
                        "-mb-0.5 border-b-2 border-primary !font-bold !text-primary": assortmentOpen,
                      })}
                      data-gtm="assortment-dropdown"
                      onClick={() => {
                        if (!searchActive) {
                          toggleAssortment()
                        }
                      }}
                      tabIndex={0}
                      aria-haspopup="true"
                    >
                      {item.title}
                      <span className={cn("assortment-dropdown-arrow", { expanded: assortmentOpen })}>
                        <ChevronDown />
                      </span>
                    </span>
                  )
                } else if (item.kind == NavbarLinksTarget.signomatic) {
                  return (
                    <a
                      key={item.id}
                      href={item.path}
                      className={cn("headerlink signomatic whitespace-nowrap", {
                        "-mb-0.5 border-b-2 border-primary !font-bold !text-primary":
                          activePage === "signomatic" && !modalOpen,
                      })}
                      data-gtm="design-page-link"
                    >
                      {item.title}
                    </a>
                  )
                } else if (item.kind == NavbarLinksTarget.quote) {
                  return (
                    <a
                      key={item.id}
                      href={item.path}
                      className={cn("headerlink quote whitespace-nowrap", {
                        "-mb-0.5 border-b-2 border-primary !font-bold !text-primary":
                          activePage === "quote" && !modalOpen,
                      })}
                      data-gtm="request-quote-link"
                    >
                      {item.title}
                    </a>
                  )
                } else {
                  return (
                    <a
                      key={item.id}
                      href={item.path}
                      className={cn("headerlink whitespace-nowrap", {
                        "-mb-0.5 border-b-2 border-primary !font-bold !text-primary":
                          activePage === item.path.split("/").pop() && !modalOpen,
                      })}
                    >
                      {item.title}
                    </a>
                  )
                }
              })}
            </>
          </nav>
          <div className="menubuttons xl:w-full">
            <div className="buttons justify-end pr-6">
              {!mobileSearchOpen && (
                <MenuSearch
                  classes={"hidden xl:block"}
                  addsearchCategories={addsearchCategories}
                  setSearchActive={setSearchActive}
                  closeAssortment={closeAssortment}
                />
              )}
              <a
                href={t("route.customer_zone")}
                className={cn("customer_zone subtle ml-3 hidden px-2 xl:flex", {
                  "-mb-0.5 border-b-2 border-primary": customerZoneActive,
                })}
                data-gtm="sign-in-link"
              >
                {currentUser ? (
                  <ProfileFill className={cn({ "text-primary": customerZoneActive })} />
                ) : (
                  <ProfileOutline className={cn({ "text-primary": customerZoneActive })} />
                )}
                <span
                  className={cn("paragraph-sm bold mb-0 whitespace-nowrap", {
                    "!font-bold !text-primary": customerZoneActive,
                  })}
                >
                  {currentUser ? t("menu.my_account") : t("customer.log_in")}
                </span>
              </a>
              <span className="divider ml-4 mr-3 hidden xl:block"></span>
              <div className="signomatic-basket-button-portal static flex h-full items-center xl:relative">
                {!loaded ? (
                  <div className="signomatic-basket-placeholder">
                    <a href={cartUrl} className="cart flex">
                      <CartOutline />
                      <span className="name paragraph-sm bold mb-0 whitespace-nowrap">{t("signslist.basket")}</span>
                    </a>
                  </div>
                ) : (
                  <div className={cn("signomatic-basket h-full", { "-mb-0.5 border-b-2 border-primary": open })}>
                    <button
                      className="cart relative flex bg-[length:35%] bg-center bg-no-repeat"
                      onClick={toggleBasket}
                      data-gtm="cart-button"
                      aria-haspopup="true"
                      type="button"
                    >
                      {count > 0 && (
                        <span
                          className={cn(
                            "cartlabel paragraph-xs bold absolute left-2 top-0.5 mb-0 h-5 w-auto min-w-5 rounded-[10px] border border-white bg-emphasis px-[5px] text-center leading-[18px] xl:top-2.5",
                            {
                              "-mt-[1px]": open,
                            }
                          )}
                        >
                          {count}
                        </span>
                      )}
                      {open ? <CartFill className="text-primary" /> : <CartOutline />}
                      <span
                        className={cn("name paragraph-sm bold mb-0 whitespace-nowrap", {
                          "!font-bold !text-primary": open,
                        })}
                      >
                        {t("signslist.basket")}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <AssortmentMenu
          categories={categories as Categories}
          assortmentOpen={assortmentOpen}
          closeAssortment={closeAssortment}
          defaultCategoryId={defaultCategoryId}
        />
      </div>

      {loaded && (
        <BasketModal
          showEmpty={showEmpty}
          businessRole={businessRole}
          toggleBasket={toggleBasket}
          count={count}
          loading={loading || refetchLoading}
          showAddedNotice={showAddedNotice}
          closeExcelImportNotice={closeExcelImportNotice}
          open={open || false}
          excelImportNotice={excelImportNotice}
          data={data}
          desktop={desktop}
          top={menuRowBottom + 11}
          activePage={activePage}
          orderId={orderId}
        />
      )}
    </>
  )
}

interface AssortmentMenuProps {
  categories: Categories
  assortmentOpen: boolean
  closeAssortment: () => void
  defaultCategoryId: string
}

const AssortmentMenu: React.FC<AssortmentMenuProps> = ({
  categories,
  assortmentOpen,
  closeAssortment,
  defaultCategoryId,
}) => {
  const { t } = useTranslation()

  const [showExtra, setShowExtra] = useState(false)
  const [activeCategoryId, setActiveCategoryId] = useState(defaultCategoryId)
  const activeCategory = categories?.find(category => category.id === activeCategoryId)
  const firstCategory = categories && categories[0]

  return (
    <div className={cn("assortment-menu basis-full", { visible: assortmentOpen })} id="assortment-menu">
      <button
        id="assortment-close"
        className="button button-subtle icon-only absolute right-4 top-4 mb-0"
        type="button"
        onClick={() => closeAssortment()}
      >
        <CloseLarge className="h-6 w-6" />
      </button>
      {categories ? (
        <div className="flex w-full">
          <div className="flex h-[500px] min-w-[320px] flex-1 flex-col overflow-y-auto border-r border-white-20 pt-6">
            {categories?.slice(1, 10).map(category => (
              <div
                key={category.id}
                className={cn(
                  "category-item flex h-10 min-h-[40px] items-center pr-4 hover:cursor-pointer hover:bg-white-5",
                  { active: category.id === activeCategoryId }
                )}
                data-gtm="dropdown-category-item"
                onClick={() => setActiveCategoryId(category.id)}
              >
                <div className="indicator mr-1 hidden h-full w-1 min-w-[4px] bg-primary"></div>
                <p className="paragraph-sm bold mb-0 ml-8 mr-4 overflow-hidden overflow-ellipsis">{category.name}</p>
                {category.id === activeCategoryId && <ArrowRight className="h-6 w-6 min-w-[24px]" />}
              </div>
            ))}
            {categories && categories.length > 10 && (
              <>
                <div className={cn({ hidden: !showExtra })}>
                  <div className="flex h-4 min-h-[16px] items-center px-8">
                    <div className="h-[1px] w-full bg-white-20"></div>
                  </div>
                  {categories?.slice(10).map(category => (
                    <div
                      key={category.id}
                      className={cn(
                        "category-item flex h-10 min-h-[40px] items-center pr-4 hover:cursor-pointer hover:bg-white-5",
                        { active: category.id === activeCategoryId }
                      )}
                      data-gtm="dropdown-category-item"
                      onClick={() => setActiveCategoryId(category.id)}
                    >
                      <div className="indicator mr-1 hidden h-full w-1 min-w-[4px] bg-primary"></div>
                      <p className="paragraph-sm bold mb-0 pl-8">{category.name}</p>
                      {category.id === activeCategoryId && <ArrowRight className="hidden h-6 w-6 min-w-[24px]" />}
                    </div>
                  ))}
                </div>
                <div
                  className="flex h-11 min-h-[44px] items-center pl-8 hover:cursor-pointer hover:bg-white-5"
                  data-gtm="dropdown-expand-link"
                  onClick={() => setShowExtra(!showExtra)}
                >
                  <p className="paragraph-sm mb-0 mr-2 max-w-min font-medium text-primary underline">
                    {showExtra ? t("examples.show_fewer_categories") : t("examples.show_all_categories")}
                  </p>
                  {showExtra ? (
                    <ArrowUp className="h-6 w-6 text-primary" />
                  ) : (
                    <ArrowDown className="h-6 w-6 text-primary" />
                  )}
                </div>
              </>
            )}
          </div>
          <div className="min-w-[400px] flex-1 border-r border-white-20 p-8">
            <div className="flex h-[390px] flex-col flex-wrap overflow-hidden">
              {activeCategory?.tags.map(tag => (
                <div className="mb-3 w-1/2 whitespace-normal" key={tag.id}>
                  <a
                    href={tag.path}
                    data-gtm="dropdown-page-item"
                    className="text-base font-medium text-black hover:text-primary-dark hover:underline"
                  >
                    {tag.pagename}
                  </a>
                </div>
              ))}
            </div>
          </div>
          <div className="w-full flex-2 p-8">
            <p className="paragraph-sm bold mb-4">{firstCategory?.name}</p>
            <div className="grid grid-cols-2 gap-4">
              {firstCategory?.tags.slice(0, 8).map(tag => (
                <a
                  href={tag.path}
                  className="flex h-20 items-center justify-center rounded-lg border-1 border-white-20 bg-white-3 decoration-black hover:border-ink-50 hover:bg-white-1 hover:underline"
                  data-gtm="dropdown-popular-item"
                  key={tag.id}
                >
                  <p className="bold center mb-0 max-w-fit whitespace-normal px-1 text-base">{tag.pagename}</p>
                </a>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex h-auto w-full items-center justify-center">
          <DotSpinner />
        </div>
      )}
      <div className="bottom-bar">
        <div className="cta">
          <span className="paragraph-sm mb-0">{t("examples.cta_header")}</span>
          <a
            href={t("route.signomatic")}
            className="button button-emphasis button-small mb-0"
            data-gtm="dropdown-design-page-button"
          >
            {t("start.signomatic_button")}
          </a>
        </div>
        <a href={t("route.products")} className="compare-products" data-gtm="dropdown-compare-products-link">
          <Reset />
          <span className="paragraph-sm mb-0">{t("menu.assortment.compare")}</span>
        </a>
      </div>
    </div>
  )
}

interface MobileMenuProps {
  categories: Categories
  siteUrl: string
  logoPath: string
  businessRole: boolean
  currentUser: boolean
  setMobileMenuOpen: () => void
  setSubmenuOpen: (open: boolean) => void
  submenuOpen: boolean
  langNl: string
  langFr: string
  langDe: string
  langIt: string
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  categories,
  siteUrl,
  logoPath,
  businessRole,
  currentUser,
  setMobileMenuOpen,
  setSubmenuOpen,
  submenuOpen,
  langNl,
  langFr,
  langDe,
  langIt,
}) => {
  const { t, i18n } = useTranslation()
  const [showExtra, setShowExtra] = useState(false)
  const [activeCategoryId, setActiveCategoryId] = useState(String)
  const activeCategory = categories?.find(category => category.id === activeCategoryId)

  return (
    <div className="menu absolute bottom-0 left-0 top-0 flex flex-col bg-white">
      <div className="menu-start h-[calc(100%_-_48px)] overflow-y-auto">
        <div className="top-section">
          <a href={siteUrl} className="logo" data-gtm="logo-link">
            <img src={logoPath} alt="logo" />
          </a>
          <span
            className="menu-close button button-large button-subtle icon-only mb-0"
            onClick={() => {
              setMobileMenuOpen()
              setSubmenuOpen(false)
              toggleScroll(false)
            }}
          >
            <CloseLarge />
          </span>
        </div>
        <div className="assortment mb-4 pt-4">
          {categories?.slice(0, 6).map(category => (
            <div
              className="category-item flex h-11 items-center px-4 hover:cursor-pointer hover:bg-white-5"
              key={category.id}
              onClick={() => {
                setSubmenuOpen(true)
                setActiveCategoryId(category.id)
              }}
              data-gtm="dropdown-category-item"
            >
              <p className="paragraph-sm mb-0 font-medium">{category.name}</p>
              <ChevronRightLarge className="h-6 w-6" />
            </div>
          ))}
          {categories && categories.length > 6 && (
            <>
              <div className={cn({ hidden: !showExtra })}>
                <div className="flex h-4 min-h-[16px] items-center px-4">
                  <div className="h-[1px] w-full bg-white-20"></div>
                </div>
                {categories?.slice(6).map(category => (
                  <div
                    className="category-item flex h-11 items-center px-4 hover:cursor-pointer hover:bg-white-5"
                    onClick={() => {
                      setSubmenuOpen(true)
                      setActiveCategoryId(category.id)
                    }}
                    data-gtm="dropdown-category-item"
                    key={category.id}
                  >
                    <p className="paragraph-sm mb-0 font-medium">{category.name}</p>
                    <ChevronRightLarge className="h-6 w-6" />
                  </div>
                ))}
              </div>
              <div
                className="flex h-11 items-center whitespace-nowrap pl-4 hover:cursor-pointer hover:bg-white-5"
                data-gtm="dropdown-expand-link"
                onClick={() => setShowExtra(!showExtra)}
              >
                <p className="paragraph-sm mb-0 mr-2 max-w-min font-medium text-primary underline">
                  {showExtra ? t("examples.show_fewer_categories") : t("examples.show_all_categories")}
                </p>
                {showExtra ? (
                  <ChevronUpLarge className="h-6 w-6 text-primary" />
                ) : (
                  <ChevronDownLarge className="h-6 w-6 text-primary" />
                )}
              </div>
            </>
          )}
        </div>
        <div className="menubuttons flex flex-col">
          <a
            href={t("route.quote")}
            className="quote flex h-11 items-center pl-4 text-ink-100"
            data-gtm="request-quote-link"
          >
            <DocumentTextOutline className="h-6 w-6" />
            <p className="paragraph-sm mb-0 ml-2 font-medium">{t("menu.quote")}</p>
          </a>
          <a
            href={t("route.customer_zone")}
            className="customer_zone flex h-11 items-center pl-4 text-ink-100"
            data-gtm="sign-in-link"
          >
            {currentUser ? (
              <>
                <ProfileFill className="h-6 w-6" />
                <span className="paragraph-sm mb-0 ml-2 font-medium">{t("menu.my_account")}</span>
              </>
            ) : (
              <>
                <ProfileOutline className="h-6 w-6" />
                <span className="paragraph-sm mb-0 ml-2 font-medium">{t("customer.log_in")}</span>
              </>
            )}
          </a>
          <a
            href={t("route.help_center")}
            className="contact flex h-11 items-center pl-4 text-ink-100"
            data-gtm="helpcenter-link"
          >
            <HeadphonesOutline className="h-6 w-6" />
            <span className="paragraph-sm mb-0 ml-2 font-medium">{t("menu.customer_service")}</span>
          </a>
        </div>
        <RoleSwitch businessRole={businessRole} mobile />
        {(i18n.language.includes("BE") || i18n.language.includes("CH")) && (
          <LanguageDropdown mobile langNl={langNl} langFr={langFr} langDe={langDe} langIt={langIt} />
        )}
      </div>
      <div className={cn("submenu z-10", { visible: submenuOpen })}>
        <div className="top-section">
          <button className="menu-back flex items-center" onClick={() => setSubmenuOpen(false)} type="button">
            <ChevronLeftLarge className="mr-2 h-6 w-6 min-w-[24px]" />
            <span className="paragraph-sm bold mb-0">{t("menu.back_to_menu")}</span>
          </button>
          <span
            className="menu-close button button-large button-subtle icon-only mb-0"
            onClick={() => {
              setMobileMenuOpen()
              setSubmenuOpen(false)
              toggleScroll(false)
            }}
          >
            <CloseLarge />
          </span>
        </div>
        <div className="assortment overflow-y-auto py-6">
          <div className="heading-four px-4">{activeCategory?.name}</div>
          <div>
            {activeCategory?.tags.map(tag => (
              <div className="flex h-11 items-center pl-4 hover:bg-white-5" key={tag.id}>
                <a
                  href={tag.path}
                  className="flex h-full w-full items-center text-base font-medium text-ink-100"
                  data-gtm="dropdown-page-item"
                >
                  {tag.pagename}
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
      <a
        href={t("route.signomatic")}
        className="cta button button-large button-emphasis full-width flat squared mb-0"
        data-gtm="dropdown-design-page-button"
      >
        <span>{t("start.signomatic_button")}</span>
      </a>
    </div>
  )
}

interface SiteMenuProps {
  trustpilotEnabled: boolean
  trustpilotBizId: string
  activePage: string
  siteUrl: string
  currentUser: boolean
  signomatic: boolean
  cartUrl: string
  businessRole: boolean
  cartCount: number
  addsearchCategories: string
  defaultCategoryId: string
  langNl: string
  langFr: string
  langDe: string
  langIt: string
  orderId?: string
}

const SiteMenu: React.FC<SiteMenuProps> = ({
  trustpilotEnabled,
  trustpilotBizId,
  siteUrl,
  currentUser,
  signomatic,
  businessRole,
  cartCount,
  addsearchCategories,
  defaultCategoryId,
  activePage,
  langNl,
  langFr,
  langDe,
  langIt,
  cartUrl,
  orderId,
}) => {
  const { i18n } = useTranslation()
  const logoPaths = import.meta.glob("@/images/layout/logos/*", { eager: true })
  const logoPath = (logoPaths[`/images/layout/logos/${i18n.language}.svg`] as any).default
  const { data } = useQuery(assortmentCategoriesDoc, {
    variables: { locale: i18n.language, business: businessRole },
    context: { retry: true },
  })

  const categories = data?.assortmentCategories
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const [submenuOpen, setSubmenuOpen] = useState(false)
  const [assortmentOpen, setAssortmentOpen] = useState(false)
  const [menuHeight, setMenuHeight] = useState(0)
  const [preMenuHeight, setPreMenuHeight] = useState(0)
  const [currentY, setCurrentY] = useState(0)

  const [setBasketState] = useMutation(setBasketStateDoc)

  const closeBasket = useCallback(() => {
    setBasketState({ variables: { open: false } })
  }, [setBasketState])

  const openBasket = useCallback(() => {
    setBasketState({ variables: { open: true, addedNotice: false } })
  }, [setBasketState])

  useEffect(() => {
    const assortmentMenu = document.querySelector<HTMLDivElement>(".assortment-menu")
    const assortmentButton = document.querySelector<HTMLSpanElement>("#assortment")
    const handleClickOutside = (event: MouseEvent) => {
      if (
        assortmentOpen &&
        assortmentMenu &&
        !assortmentMenu.contains(event.target as Node) &&
        assortmentButton &&
        !assortmentButton.contains(event.target as Node)
      ) {
        setAssortmentOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [wrapperRef, assortmentOpen])

  useEffect(() => {
    const height = window.innerWidth < 1280 ? 48 : 64

    setMenuHeight(height)
    setCurrentY(-height)
  }, [])

  useEffect(() => {
    const trustpilotHeight = window.innerWidth < 1280 ? 0 : 40
    const infoBannerHeight = document.querySelector<HTMLDivElement>(".info_banner")?.offsetHeight || 0

    setPreMenuHeight(trustpilotHeight + infoBannerHeight)
  }, [])

  const menurowRef = useRef<HTMLDivElement>(null)

  const [lastScrollTop, setLastScrollTop] = useState(0)
  const [menuVisible, setMenuVisible] = useState(true)

  useEffect(() => {
    const handleScroll = () => {
      if (
        !menurowRef.current ||
        document.querySelector("#signomatic-root") ||
        document.querySelector(".minimatic-mobile-dropdown")
      ) {
        return
      }

      const scrollTop = document.body.scrollTop || document.documentElement.scrollTop
      const scrollDifference = scrollTop - lastScrollTop
      const y = currentY - scrollDifference
      let translateY

      if (scrollDifference < 0) {
        // SCROLLING UP

        if (scrollTop <= preMenuHeight) {
          // trustpilot visible
          const min = -menuHeight

          translateY = Math.min(min, y)
        } else if (!menuVisible) {
          const min = -(menuHeight + preMenuHeight)

          translateY = Math.min(min, y)
        } else {
          // keep the same
          translateY = currentY
        }
      } else if (scrollDifference > 0) {
        // SCROLLING DOWN

        const searchResults = document.querySelector<HTMLDivElement>("#addsearch-results")
        // keep menu sticky if search results are visible
        const max = searchResults?.classList.contains("hidden")
          ? -(menuHeight * 2 + preMenuHeight + 1)
          : -(menuHeight + preMenuHeight + 1)
        translateY = Math.max(max, y)

        if (y <= max) {
          setMenuVisible(false)
        }

        if (menuHeight == 64) {
          // desktop
          closeBasket()
        }

        // reset assortment
        setAssortmentOpen(false)
      }

      if (document.body.getBoundingClientRect().top >= 0) {
        // top of the page
        translateY = -menuHeight
      }

      if (translateY) {
        menurowRef.current.style.transform = `translateY(${translateY}px)`
        setCurrentY(translateY)
      }

      setLastScrollTop(scrollTop)
    }

    window.addEventListener("scroll", handleScroll)

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [menuHeight, menurowRef, lastScrollTop, currentY, menuVisible, closeBasket, preMenuHeight])

  useEffect(() => {
    const showMenu = () => {
      const exampleToCart = document.querySelectorAll(".example-to-cart")

      for (const exampleButton of Array.from(exampleToCart)) {
        exampleButton.addEventListener("click", e => {
          e.preventDefault()

          if (window.innerWidth < 1280) {
            return
          }

          if (menurowRef.current) {
            const translate = -(menuHeight + preMenuHeight)
            setMenuVisible(true)

            menurowRef.current.style.transform = `translateY(${translate}px)`
            setCurrentY(translate)
          }
        })
      }
    }

    showMenu()

    const displayExamples = document.querySelector<HTMLButtonElement>(".display-examples")

    displayExamples?.addEventListener("click", e => {
      e.preventDefault()

      setTimeout(() => {
        showMenu()
      }, 1000)
    })
  }, [menuHeight, preMenuHeight])

  useEffect(() => {
    if (storageLocal.getItem("update_sign") === "true") {
      openBasket()

      if (!signomatic) {
        storageLocal.removeItem("update_sign")
      }
    }
  }, [openBasket, signomatic])

  return (
    <>
      <header className="mainmenu relative" ref={wrapperRef}>
        <PreMenurow
          trustpilotEnabled={trustpilotEnabled}
          trustpilotBizId={trustpilotBizId}
          businessRole={businessRole}
          langNl={langNl}
          langFr={langFr}
          langDe={langDe}
          langIt={langIt}
        />
        <div className="menurow-filler border-interactive h-12 border-b-1 xl:h-16"></div>
        <Menurow
          siteUrl={siteUrl}
          activePage={activePage}
          currentUser={currentUser}
          loadBasket={signomatic}
          showEmpty={!signomatic}
          cartCount={cartCount}
          addsearchCategories={addsearchCategories}
          logoPath={logoPath}
          setMobileSearchOpen={() => setMobileSearchOpen(!mobileSearchOpen)}
          mobileSearchOpen={mobileSearchOpen}
          setMobileMenuOpen={() => setMobileMenuOpen(true)}
          toggleAssortment={() => setAssortmentOpen(!assortmentOpen)}
          assortmentOpen={assortmentOpen}
          closeAssortment={() => setAssortmentOpen(false)}
          defaultCategoryId={defaultCategoryId}
          categories={categories as Categories}
          classes={cn("fixed z-30 translate-y-[-48px] xl:translate-y-[-64px]", {
            "xl:absolute": lastScrollTop == 0,
          })}
          menurowRef={menurowRef}
          businessRole={businessRole}
          cartUrl={cartUrl}
          menuRowBottom={currentY + preMenuHeight + menuHeight * 2}
          orderId={orderId}
        />

        <div
          className={cn(
            "search-bar fixed left-0 right-0 z-20 flex h-16 -translate-y-full items-center justify-center",
            {
              visible: mobileSearchOpen,
            }
          )}
          style={{ top: currentY + preMenuHeight + menuHeight * 2 }}
        >
          {mobileSearchOpen && <MenuSearch addsearchCategories={addsearchCategories} />}
        </div>

        <div
          className={cn("trustpilotrow relative flex h-10 items-center lg:hidden", { "signomatic hidden": signomatic })}
        >
          {trustpilotEnabled && (
            <TrustpilotWidget trustpilotBizId={trustpilotBizId} classes="flex-1 mt-3" height="35px" width="100%" />
          )}
        </div>
      </header>
      <header className={cn("mainmenu-mobile z-40", { visible: mobileMenuOpen })}>
        <div
          className="overlay"
          onClick={() => {
            setMobileMenuOpen(false)
            setSubmenuOpen(false)
            toggleScroll(false)
          }}
        ></div>
        <MobileMenu
          categories={categories as Categories}
          siteUrl={siteUrl}
          logoPath={logoPath}
          businessRole={businessRole}
          currentUser={currentUser}
          setMobileMenuOpen={() => setMobileMenuOpen(false)}
          setSubmenuOpen={setSubmenuOpen}
          submenuOpen={submenuOpen}
          langNl={langNl}
          langFr={langFr}
          langDe={langDe}
          langIt={langIt}
        />
      </header>
    </>
  )
}

export default SiteMenu
